import {
  AbilityBuilder,
  AbilityClass,
  ConditionsMatcher,
  mongoQueryMatcher,
  PureAbility,
} from '@casl/ability';
import type { Ability } from 'abi-opr-common-types';
import { MeetingStatusEnum, ModuleEnum } from 'abi-opr-common-types';

import type { GetMeetingSelectData } from '~/entities/Meeting/api/useGetMeeting';

import { rankBand } from '../utils/rankBand';

import { OprAuthenticatedUser } from './types/session';

enum Action {
  List = 'list',
  Create = 'create',
  View = 'view',
  Update = 'update',
  Manage = 'manage',
  Vote = 'vote',
  Facilitate = 'facilitate',
  Proxy = 'proxy',
  Unlock = 'unlock',
  Skip = 'skip',
  Delete = 'delete',
}

export type Entities =
  | 'Meeting'
  | 'User'
  | 'EligibilityCriteriaSetup'
  | 'EligibilityCriteriaExceptions'
  | 'RoleSetting'
  | 'Translations'
  | 'Home'
  | 'OprProcess'
  | 'PersonalDashboard'
  | 'PersonalTalentCard'
  | 'ManagerDashboard'
  | 'ManagerTalentCard'
  | 'ManagerAssessment'
  | 'ManagerFeedback'
  | 'HelperFooter'
  | 'ReportTalentSearch'
  | 'ReportMyOrganization'
  | 'ReportTalentPerformance'
  | 'ReportTalentPool'
  | 'ReportOpr'
  | 'ReportMeetingDetails'
  | 'TalentSearch'
  | 'Documents'
  | 'FeatureFlags'
  | 'Cheershub'
  | 'Rewards'
  | 'Compensation'
  | 'PersonalDashboardV2'
  | 'MyLCM'
  | 'Stellar'
  | 'Succession'
  | 'TalentPoolManagement'
  | `${ModuleEnum}`;

export type AbilityTuple = [`${Action}`, Entities | Record<string, unknown>];

export type AppAbility = PureAbility<AbilityTuple>;

export type Subjects = {
  meeting?: Pick<
    GetMeetingSelectData,
    'id' | 'isVoter' | 'isFacilitator' | 'isOwner' | 'zone' | 'status'
  >;
  talentCard?: { allowAccess: boolean };
  dashboard?: { allowAccess: boolean };
};

const allowedZonesToApps = {
  cheershub: [
    'MAZ',
    'SAZ',
    'GHQ',
    'GLO',
    'NO-ZONE',
    'GLOBAL',
    'ZONE MIDDLE AMERICAS',
    'ZONE SOUTH AMERICA',
    'ZONE EUROPE',
    'ZONE AFRICA',
    'ZONE ASIA PACIFIC',
  ],
  rewards: [
    'EUR',
    'SAZ',
    'GHQ',
    'GLO',
    'NO-ZONE',
    'GLOBAL',
    'ZONE SOUTH AMERICA',
    'ZONE EUROPE',
  ],
  compensation: [
    'AFR',
    'MAZ',
    'NAZ',
    'EUR',
    'ZONE MIDDLE AMERICAS',
    'ZONE EUROPE',
    'APAC',
    'GHQ',
    'GLOBAL',
  ],
};

export function createUserAbility(
  user?: OprAuthenticatedUser,
  permissions: Subjects = {},
  rules?: AppAbility['_indexedRules']
) {
  const { can, build } = new AbilityBuilder(
    PureAbility as AbilityClass<AppAbility>
  );

  const originalUserRoles = user?.role ?? ({} as OprAuthenticatedUser['role']);

  const userRoles =
    user?.proxiedAs?.role ?? user?.role ?? ({} as OprAuthenticatedUser['role']);

  const userAbilities = user?.proxiedAs?.abilities ?? user?.abilities ?? [];
  const isManager = userRoles?.isManager ?? false;
  const isEligible = user?.proxiedAs?.eligible ?? user?.eligible;
  const has10x = user?.proxiedAs?.has10x ?? user?.has10x;
  const isVoter = user?.proxiedAs?.isVoter ?? user?.isVoter;
  const canCreateMeeting =
    user?.proxiedAs?.canCreateMeeting ?? user?.canCreateMeeting ?? false;
  const isFacilitator =
    user?.proxiedAs?.isFacilitator ?? user?.isFacilitator ?? false;

  // Map user Abilities
  (userAbilities as Ability[]).forEach((ability: Ability) => {
    can(ability.action, ability.module);
  });

  if (canCreateMeeting) {
    can(Action.Create, 'Meeting');
  }

  can(Action.View, 'PersonalTalentCard');
  can(Action.View, 'Documents');

  if (isEligible) {
    can(Action.View, 'Home');
  }

  if (
    allowedZonesToApps.cheershub.includes(
      (user?.proxiedAs?.zone || user?.zone) ?? ''
    )
  ) {
    can(Action.View, 'Cheershub');
  }

  if (
    allowedZonesToApps.rewards.includes(
      (user?.proxiedAs?.zone || user?.zone) ?? ''
    ) &&
    rankBand((user?.proxiedAs?.bandGroup || user?.bandGroup) ?? '') <= 4
  ) {
    can(Action.View, 'Rewards');
  }

  can(Action.View, 'Compensation');

  if (rankBand((user?.proxiedAs?.bandGroup || user?.bandGroup) ?? '') <= 7) {
    can(Action.View, 'MyLCM');
  }

  if (originalUserRoles.isSuperAdmin || originalUserRoles.isGlobalAdmin) {
    can(Action.Proxy, 'User');
  }

  if (has10x && isEligible) {
    can(Action.View, 'PersonalDashboard');
  }

  if (isVoter || isFacilitator) {
    can(Action.List, 'Meeting');
    can(Action.View, 'OprProcess');
  }

  if (has10x) {
    can(Action.View, 'PersonalDashboardV2');
  }

  // Manager roles
  if (isManager) {
    can(Action.View, [
      'Home',
      'ManagerAssessment',
      ModuleEnum.GetEmployeeFeedbackDetailModelView,
      'ReportTalentSearch',
      'OprProcess',
      'ReportTalentPerformance',
      'ReportMyOrganization',
    ]);
    can(Action.List, ModuleEnum.GetEmployeeFeedbackModelView);
    can(Action.Update, ModuleEnum.Feedback);
  }

  // Succession Access
  if (userRoles.isGlobalAdmin) {
    can(Action.View, 'Succession');
    can(Action.Unlock, 'Succession');
    can(Action.Update, 'Succession');
  }

  if (userRoles.isPBP || userRoles.isZoneAdmin) {
    can(Action.View, 'Succession');
  }

  // Super Admin
  if (userRoles.isSuperAdmin) {
    can(Action.Create, [ModuleEnum.FeedbackConfiguration]);
    can(Action.View, [
      'Home',
      ModuleEnum.GetEmployeeFeedbackDetailModelView,
      'HelperFooter',
      'OprProcess',
      'ReportTalentSearch',
      'ReportTalentPerformance',
      'ReportTalentPool',
      'ReportOpr',
      'ReportMeetingDetails',
      'ReportMyOrganization',
      'ManagerAssessment',
      'EligibilityCriteriaSetup',
      'Meeting',
      'EligibilityCriteriaExceptions',
      'TalentPoolManagement',
    ]);
    can(Action.Update, ModuleEnum.Feedback);
    can(Action.List, [
      'Meeting',
      ModuleEnum.GetEmployeeFeedbackModelView,
      ModuleEnum.FeedbackConfiguration,
    ]);
    can(Action.Manage, ['Translations', 'RoleSetting']);
    can(Action.Unlock, 'ManagerAssessment');
    can(Action.Skip, 'FeatureFlags');
    can(Action.Delete, ['Meeting', ModuleEnum.FeedbackConfiguration]);
    can(Action.Facilitate, 'Meeting');
    can(Action.Update, 'EligibilityCriteriaSetup');
  }

  // Global Admin
  if (userRoles.isGlobalAdmin) {
    can(Action.Create, [ModuleEnum.FeedbackConfiguration]);
    can(Action.Update, ModuleEnum.Feedback);
    can(Action.List, [
      'Meeting',
      ModuleEnum.GetEmployeeFeedbackModelView,
      ModuleEnum.FeedbackConfiguration,
    ]);
    can(Action.View, [
      'Home',
      ModuleEnum.GetEmployeeFeedbackDetailModelView,
      'HelperFooter',
      'ReportTalentSearch',
      'OprProcess',
      'ReportTalentPerformance',
      'ReportTalentPool',
      'ReportOpr',
      'ReportMeetingDetails',
      'ReportMyOrganization',
      'ManagerAssessment',
      'EligibilityCriteriaSetup',
      'Meeting',
      'EligibilityCriteriaExceptions',
      'TalentPoolManagement',
    ]);
    can(Action.Update, 'EligibilityCriteriaSetup');
    can(Action.Manage, ['Translations', 'RoleSetting']);
    can(Action.Unlock, 'ManagerAssessment');
    can(Action.Skip, 'FeatureFlags');
    can(Action.Delete, ['Meeting', ModuleEnum.FeedbackConfiguration]);
    can(Action.Facilitate, 'Meeting');
    can(Action.View, 'Stellar');
  }

  // Zone Admin
  if (userRoles.isZoneAdmin) {
    can(Action.Update, ModuleEnum.Feedback);
    can(Action.List, ['Meeting', ModuleEnum.GetEmployeeFeedbackModelView]);
    can(Action.View, [
      'OprProcess',
      'HelperFooter',
      'ReportTalentSearch',
      'ReportTalentPerformance',
      'ReportTalentPool',
      'ReportOpr',
      'ReportMeetingDetails',
      'ReportMyOrganization',
      'ManagerAssessment',
      'Home',
      'EligibilityCriteriaExceptions',
      ModuleEnum.GetEmployeeFeedbackDetailModelView,
    ]);

    can(Action.Skip, 'FeatureFlags');

    if (permissions.meeting) {
      // When viewing a specific meeting, the API already performs the
      // authoritative access check (zone + SLT scope). If the meeting
      // data was returned successfully, the zone admin has access.
      // However, ZTA actions (Facilitate/Delete) should only apply when
      // the API confirms facilitator-level access (zone/SLT scope match),
      // not when the ZTA is merely a voter in the meeting.
      if (permissions.meeting.isFacilitator) {
        can([Action.View, Action.Delete, Action.Facilitate], 'Meeting');
      }
    }
  }

  // PBP
  if (userRoles.isPBP) {
    can(Action.Update, ModuleEnum.Feedback);
    can(Action.View, [
      'Home',
      'ManagerAssessment',
      ModuleEnum.GetEmployeeFeedbackDetailModelView,
    ]);
    can(Action.List, [ModuleEnum.GetEmployeeFeedbackModelView, 'Meeting']);
    can(Action.View, [
      'Meeting',
      'HelperFooter',
      'OprProcess',
      'ReportTalentSearch',
      'ReportTalentPerformance',
      'ReportTalentPool',
      'ReportOpr',
      'ReportMeetingDetails',
      'ReportMyOrganization',
    ]);

    can(Action.Delete, 'Meeting');
    can(Action.Skip, 'FeatureFlags');
    can(Action.View, 'Stellar');
  }

  // Talent Manager
  if (userRoles.isTalentManager) {
    can(Action.View, 'ReportMeetingDetails');
  }

  const featureFlagManagementAllowedUsers = [
    'lais.bolognesi@ab-inbev.com',
    'otavio.reis-ext@ab-inbev.com',
    'rodrigo.mello-ext@ab-inbev.com',
    'thiago.oliveira-ext3@ab-inbev.com',
    'wesley.prado-ext@ab-inbev.com',
    'paola.kloch-ext@ab-inbev.com',
    'davys.sousa-ext@ab-inbev.com',
    'danton.heuer-ext@ab-inbev.com',
  ];
  if (user?.email && featureFlagManagementAllowedUsers.includes(user.email)) {
    can(Action.Manage, 'FeatureFlags');
  }

  if (permissions.talentCard?.allowAccess) {
    can(Action.View, 'ManagerTalentCard');
  }

  if (permissions.dashboard?.allowAccess) {
    can(Action.View, 'ManagerDashboard');
  }

  if (permissions.meeting) {
    if (
      (permissions.meeting.status as string) !==
        MeetingStatusEnum.NOT_STARTED &&
      permissions.meeting.isVoter
    ) {
      can(Action.View, 'Meeting', { id: permissions.meeting.id });
      can(Action.Vote, 'Meeting', { id: permissions.meeting.id });
    }

    if (permissions.meeting.isFacilitator) {
      can(Action.View, 'Meeting', { id: permissions.meeting.id });
      can(Action.Facilitate, 'Meeting', { id: permissions.meeting.id });
    }

    if (permissions.meeting.isOwner) {
      can(Action.Delete, 'Meeting', { id: permissions.meeting.id });
      can(Action.View, 'Meeting', { id: permissions.meeting.id });
      can(Action.Facilitate, 'Meeting', { id: permissions.meeting.id });
    }
  }

  const ability = build({
    // eslint-disable-next-line no-underscore-dangle
    detectSubjectType: (object: Record<string, 'Meeting'>) => object.__typename,
    conditionsMatcher: mongoQueryMatcher as ConditionsMatcher<unknown>,
  });

  if (rules) {
    ability.update(rules);
  }

  return ability;
}
